import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@material-ui/core';
import { Avatar, Button, Chip } from '@orijinworks/frontend-commons';
import { COLORS } from '../constants';
import { getPersonDisplayName } from '../persons-manager.helpers';
import { usePersonCardStyles } from './person-card.style';

const AVATAR_SIZE = 40;

const personPropType = PropTypes.shape({
  personId: PropTypes.number.isRequired,
  displayFirstName: PropTypes.string,
  firstName: PropTypes.string,
  displayLastName: PropTypes.string,
  lastName: PropTypes.string,
  username: PropTypes.string,
  docId: PropTypes.string,
  email: PropTypes.string,
});

const attributeRowPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  attributeName: PropTypes.string,
  value: PropTypes.string,
});

const attributesStatePropType = PropTypes.shape({
  status: PropTypes.oneOf(['loading', 'idle', 'ok', 'error']),
  items: PropTypes.arrayOf(attributeRowPropType),
});

const PersonCardInner = ({ person, attributesState, onEdit }) => {
  const classes = usePersonCardStyles();
  /** @type {string} */
  const name = getPersonDisplayName(person);
  const first = person.displayFirstName || person.firstName || '';
  const last = person.displayLastName || person.lastName || '';

  const { status, items } = attributesState || { status: 'loading', items: [] };

  return (
    <Box className={classes.root}>
      <Box className={classes.topRow}>
        <Avatar
          renderState="initials"
          firstName={first || name}
          lastName={last || '.'}
          iconSize={AVATAR_SIZE}
          decorative
          avatarProps={{
            backgroundColor: COLORS.primary,
            textColor: '#fff',
          }}
        />
        <Box className={classes.identity}>
          <Typography className={classes.name} component="div">
            {name}
          </Typography>
          <Typography className={classes.meta} component="div">
            {person.username && (
              <Typography component="span" display="inline" className={classes.meta}>
                @{person.username}
              </Typography>
            )}
            {person.docId && (
              <Typography component="span" display="inline" className={classes.meta}>
                {person.username ? ' · ' : ''}
                Doc ID: {person.docId}
              </Typography>
            )}
          </Typography>
          {person.email && (
            <Typography className={classes.meta} component="div">
              {person.email}
            </Typography>
          )}
        </Box>
        <Box className={classes.editBtn}>
          <Button variant="outlined" size="small" color="primary" onClick={() => onEdit(person)}>
            Edit
          </Button>
        </Box>
      </Box>
      <Box className={classes.chips}>
        {(status === 'loading' || status === 'idle') && (
          <Typography variant="caption" className={classes.meta}>
            Loading attributes…
          </Typography>
        )}
        {status === 'error' && (
          <Typography variant="caption" color="error">
            Could not load attributes
          </Typography>
        )}
        {status === 'ok' && items.length === 0 && (
          <Typography variant="caption" className={classes.meta}>
            No person attributes
          </Typography>
        )}
        {status === 'ok' &&
          items.map((row) => (
            <Chip
              key={row.id}
              className={classes.attributeChip}
              classes={{ label: classes.attributeChipLabel }}
              size="small"
              variant="outlined"
              color="primary"
              label={
                <>
                  <Typography component="span" className={classes.attributeName} display="inline">
                    {row.attributeName}:
                  </Typography>{' '}
                  <Typography component="code" className={classes.attributeValueCode} display="inline">
                    {row.value ?? ''}
                  </Typography>
                </>
              }
            />
          ))}
      </Box>
    </Box>
  );
};

PersonCardInner.propTypes = {
  person: personPropType.isRequired,
  attributesState: attributesStatePropType,
  onEdit: PropTypes.func.isRequired,
};

PersonCardInner.defaultProps = {
  attributesState: undefined,
};

export const PersonCard = React.memo(PersonCardInner);
PersonCard.displayName = 'PersonCard';
